import Alert from '@xo-union/tk-component-alerts';
import Spinner from '@xo-union/tk-component-spinner';
import { FormikValues } from 'formik';
import React, { ChangeEvent, SyntheticEvent, useRef, useState } from 'react';

import { getImageFile, imageFileToImgElement } from '../../../helpers/images/processImages';
import { loadImage, useImageUploader, ValidationOptions } from '../../../helpers/images/useImageUploader';
import LinkButton from '../../LinkButton';
import PhotoCropper from '../../Modals/PhotoCropper';
import PhotoPlaceholder from '../../PhotoUpload/PhotoPlaceholder';
import styles from './index.scss';

// https://developer.mozilla.org/en-US/docs/Web/Media/Guides/Formats/Image_types
export const imageFileTypesWithoutGif = [
  'image/apng',
  'image/bmp',
  'image/jpeg',
  'image/pjpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'image/webp',
  'image/x-icon',
];

interface FieldProps {
  value: string;
  name: string;
}
interface Props {
  field: FieldProps;
  form: FormikValues;
  pageId: number;
  parentItemType: string;
  imageFieldName: string;
  imageTestId?: string;
  imageOptions?: ValidationOptions;
}
interface CropBox {
  x: number;
  y: number;
  width: number;
  height: number;
}
export interface CropProps {
  file: File;
  crop: CropBox;
  imagePreview: string;
  rotation: number;
}

interface CropperFileState {
  cropperFile?: HTMLImageElement;
  isNewImage?: boolean;
}

export const ImageField: React.FC<Props> = ({
  field,
  form,
  pageId,
  parentItemType,
  imageFieldName,
  imageTestId,
  imageOptions,
}) => {
  const { value: photoPath } = field;
  const { setFieldValue, values } = form;
  const [loading, setLoading] = useState(false);
  const alertRef = useRef<any>(null);
  const { imageUpload } = useImageUploader();
  const [fileState, setCropperFile] = useState<CropperFileState>();
  const { cropperFile, isNewImage } = fileState || {};

  const openCropper = (cropperFileState: CropperFileState) => {
    setCropperFile(cropperFileState);
  };

  const closeCropper = () => {
    setCropperFile({ cropperFile: undefined, isNewImage: undefined });
  };

  const onSelectFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || !files[0]) return;
    const file = files[0];

    setLoading(true);
    const image = await loadImage(file, imageOptions);
    setLoading(false);

    if (image.error || !image.img) {
      showError(image?.error?.message || 'There was an error uploading your image. Try again.');
      return;
    }

    openCropper({ cropperFile: image.img, isNewImage: true });
  };

  const onEditPhoto = async (e: SyntheticEvent) => {
    e.preventDefault();

    setLoading(true);
    const file = await getImageFile(photoPath);
    const img = await imageFileToImgElement(file);
    setLoading(false);

    openCropper({ cropperFile: img, isNewImage: false });
  };

  const saveImage = async ({ file, crop, imagePreview, rotation }: CropProps) => {
    let mediaApiId = values[imageFieldName]?.mediaApiId;
    let rotationAdjusted: number | null = isNewImage ? null : rotation;
    const imageExistedBefore = !!mediaApiId;

    try {
      let res;
      if (!imageExistedBefore) {
        res = await imageUpload({ file }, imageOptions);
        mediaApiId = res.data?.mediaApiId;
      }
      if (res?.error) {
        closeCropper();
        showError(res.error.message);
        return;
      }

      setFieldValue(`${imageFieldName}.mediaApiId`, mediaApiId, false);
      setFieldValue(`${imageFieldName}.mediaUrl`, imagePreview, false);
      setFieldValue(`${imageFieldName}.cropX`, crop.x, false);
      setFieldValue(`${imageFieldName}.cropY`, crop.y, false);
      setFieldValue(`${imageFieldName}.height`, crop.height, false);
      setFieldValue(`${imageFieldName}.width`, crop.width, false);
      setFieldValue(`${imageFieldName}.rotation`, rotationAdjusted, false);
      closeCropper();
    } catch (err) {
      showError(err?.message);
    }
  };

  const showError = (message: string) => {
    alertRef.current.showAndDismiss({
      type: 'error',
      content: message,
      waitTime: 3000,
    });
  };

  const onDeletePhoto = (e: SyntheticEvent) => {
    e.preventDefault();

    setFieldValue(imageFieldName, {}, false);
    closeCropper();
  };

  return (
    <>
      <label>
        <>
          <input
            type="file"
            data-testid={imageTestId}
            multiple={false}
            style={{ display: 'none' }}
            onChange={onSelectFile}
            accept={imageFileTypesWithoutGif.join(',')}
          />
          {photoPath ? (
            <div className={styles.currentImage}>
              {loading ? (
                <div className={styles.loading}>
                  <Spinner data-testid="loading-spinner" />
                </div>
              ) : (
                <div className={styles.imageContainer}>
                  <img data-testid="photo-upload-image" src={photoPath} className={styles.image} alt="upload" />
                  <Spinner className={styles.spinner} data-testid="image-spinner" />
                </div>
              )}
              <div className={styles.currentActions}>
                <LinkButton data-testid="page-item-edit-photo" onClick={onEditPhoto}>
                  Edit Photo
                </LinkButton>
                <LinkButton data-testid="page-item-remove-photo" onClick={onDeletePhoto}>
                  Remove
                </LinkButton>
              </div>
            </div>
          ) : (
            <PhotoPlaceholder />
          )}
        </>
      </label>
      <Alert ref={alertRef} position="bottom" />
      {cropperFile && (
        <PhotoCropper
          cropperInfo={{
            file: cropperFile,
            onSave: saveImage,
          }}
          onCancel={closeCropper}
          pageId={pageId}
          itemType={parentItemType}
          primaryActionText={photoPath ? 'Crop Photo' : 'Upload Photo'}
        />
      )}
    </>
  );
};
